import throttle from 'raf-throttle'
import {
  template,
  UICorePlugin,
  EventMap,
  AttributeMap,
  TemplateStringifier,
  SupportedVersion
} from '@clappr/core'
import {LevelSelector} from 'common/plugins/level-selector'
import {MediaControl} from 'common/plugins/media-control'
import {Subtitles} from 'common/plugins/subtitles'
import {SUBTITLES_OFF_ID} from 'common/plugins/subtitles/subtitles'
import {Share} from 'common/plugins/share'
import {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import {SubtitleKey} from 'common/api/records'
import styles from './styles.scss'
import {Scrollbar} from './scrollbar'
import pluginHtml from './template'
import PlaybackRatePlugin from 'clappr-playback-rate-plugin'

const EMPTY_ARR: never[] = []

export class SettingsMenu<T extends Options> extends UICorePlugin<T> {
  private scrollbar?: Scrollbar

  public get name(): string {
    return 'settingsMenu'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get template(): TemplateStringifier {
    return template(pluginHtml)
  }

  public get attributes(): AttributeMap {
    return {
      class: `${styles.settings} eplayer-skin-shiftable eplayer-skin-fullsize`,
      'data-settings-menu': ''
    }
  }

  public get events(): EventMap {
    return {
      'click [data-menu-close]': 'close',
      'click [data-level]': 'onLevelSelect',
      'click [data-rate]': 'onRateSelect',
      'click [data-subtitle]': 'onSubtitleSelect'
    }
  }

  private get levelSelector(): LevelSelector<T> | null {
    return this.core.getPlugin('levelSelector') as LevelSelector<T> | null
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get subtitles(): Subtitles<T> | null {
    return this.core.getPlugin('subtitles') as Subtitles<T> | null
  }

  private get share(): Share<T> | null {
    return this.core.getPlugin('share') as Share<T> | null
  }

  private get playbackRate(): PlaybackRatePlugin<T> | null {
    return this.core.getPlugin('playbackRate') as PlaybackRatePlugin<T> | null
  }

  private onClickOutside = (ev: MouseEvent): void => {
    if (this.core.$el.hasClass('settings-menu-visible')) {
      const target = ev.target as HTMLDivElement

      if (
        !target.hasAttribute('data-settings-menu') &&
        (target.closest('[data-settings-menu]') ||
          target.closest('[data-menu="menu"]') ||
          target.closest('[data-menu="levels"]') ||
          target.closest('[data-menu="rates"]') ||
          target.closest('[data-menu="subtitles"]'))
      ) {
        return
      }

      this.close()
    }
  }

  public bindEvents(): void {
    const {activePlayback} = this.core

    document.addEventListener('click', this.onClickOutside)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (activePlayback) {
      this.listenTo(
        activePlayback,
        Events.PLAYBACK_BITRATE,
        this.highlightCurrentLevel
      )
      this.listenTo(
        activePlayback,
        Events.PLAYBACK_LEVEL_SWITCH_START,
        this.highlightCurrentLevel
      )
    }

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_MENU_CLICK,
        this.show
      )
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    if (this.levelSelector) {
      this.listenTo(
        this.levelSelector,
        Events.LEVEL_SELECTOR_RENDERED,
        this.render
      )
    }

    if (this.share) {
      this.listenTo(this.share, Events.SHARE_OPEN, this.close)
    }

    this.listenTo(this.core, Events.CORE_RESIZE, this.onParentResize)
  }

  public stopListening(
    object?: unknown,
    name?: string,
    callback?: (event: any) => void
  ): this {
    if (!callback) {
      document.removeEventListener('click', this.onClickOutside)
    }

    return super.stopListening(object, name, callback)
  }

  protected onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onParentResize = throttle(() => {
    this.scrollbar?.update()
  })

  protected show(section: string): void {
    const $element = this.$el.find(`[data-menu-section="${section}"]`)

    if ($element.is(':visible')) {
      this.close()
    } else {
      this.mediaControl?.$el
        .find('[data-menu-item-active]')
        .removeAttr('data-menu-item-active')
      this.core.$el.toggleClass('settings-menu-visible', true)
      this.$el.show()

      this.$el.find('[data-menu-section]').hide()
      $element.show()
      this.mediaControl?.$el
        .find(`[data-menu="${section}"]`)
        .attr('data-menu-item-active', '')

      this.scrollbar ??= new Scrollbar(this.$el.find('[data-menu-inner]'))
      this.scrollbar.update()

      if (section === 'rates') {
        this.scrollbar.scrollToBottom()
      }
    }
  }

  protected close(): void {
    this.$el.hide()
    this.$el.find('[data-menu-section]').hide()
    this.core.$el.toggleClass('settings-menu-visible', false)
    this.mediaControl?.$el
      .find('[data-menu-item-active]')
      .removeAttr('data-menu-item-active')
    this.trigger(Events.SETTINGS_MENU_CLOSE)
  }

  protected onLevelSelect(event: Event): void {
    const {level = ''} = (event.target as HTMLElement).dataset
    const levelId = parseInt(level, 10)

    this.levelSelector?.setLevel(levelId)

    this.close()
    this.highlightCurrentLevel()

    this.trigger(Events.SETTINGS_MENU_LEVEL_SELECTOR_CLICK, level)
  }

  protected onRateSelect(event: Event): void {
    const {rate} = (event.target as HTMLElement).dataset

    this.playbackRate?.setSelectedRate(rate as string)

    this.close()
    this.highlightCurrentRate()

    this.trigger(Events.SETTINGS_MENU_RATE_SELECTOR_CLICK, rate)
  }

  public async onSubtitleSelect(event: Event): Promise<void> {
    const {subtitle} = (event.currentTarget as HTMLElement).dataset

    await this.subtitles?.updateSubtitles(Number(subtitle))

    this.close()
    this.highlightCurrentSubtitle()

    this.trigger(
      Events.SETTINGS_MENU_SUBTITLE_SELECTOR_CLICK,
      this.subtitles?.currentSubtitles.language ?? SubtitleKey.DISABLED
    )
  }

  private highlightCurrentLevel(): void {
    const $items = this.$el.find('[data-level]')
    const {levelSelector} = this

    if (levelSelector && $items.length) {
      const $active = this.$el.find(
        `[data-level="${levelSelector.selectedLevel}"]`
      )

      $items.toggleClass(styles.active, false)
      $active.toggleClass(styles.active, true)
      this.$el.find('[data-level-label]').text($active.text())
    }
  }

  private highlightCurrentRate(): void {
    const $items = this.$el.find('[data-rate]')
    const {playbackRate} = this

    if (playbackRate && $items.length) {
      const $active = this.$el.find(
        `[data-rate="${playbackRate.currentSelectedRate}"]`
      )

      $items.toggleClass(styles.active, false)
      $active.toggleClass(styles.active, true)
      this.$el.find('[data-rate-label]').text($active.text())
    }
  }

  private highlightCurrentSubtitle(): void {
    const $items = this.$el.find('[data-subtitle]')

    if ($items.length) {
      const $active = this.$el.find(
        `[data-subtitle="${
          this.subtitles?.currentSubtitles.id ?? SUBTITLES_OFF_ID
        }"]`
      )

      $items.toggleClass(styles.active, false)
      $active.toggleClass(styles.active, true)
      this.$el.find('[data-subtitle-label]').text($active.text())
    }
  }

  public destroy(): void {
    this.scrollbar?.destroy()
    delete this.scrollbar
    super.destroy()
  }

  public render(): this {
    const {share} = this.core.options

    if (this.mediaControl) {
      const rates = this.playbackRate?.playbackRatesList || EMPTY_ARR
      const levels = this.levelSelector?.levelsList || EMPTY_ARR
      const subtitles = this.subtitles?.subtitlesList || EMPTY_ARR

      const pluginsCount = [
        rates.length,
        levels.length,
        subtitles.length
      ].filter(Boolean).length

      this.core.$el
        .toggleClass('settings-menu-multiple', pluginsCount > 1)
        .toggleClass('settings-menu-with-rates', !!rates.length)
        .toggleClass('settings-menu-with-levels', !!levels.length)
        .toggleClass('settings-menu-with-subtitles', !!subtitles.length)

      if (pluginsCount > 0) {
        this.$el.html(
          this.template({
            levels,
            rates,
            share,
            subtitles,
            i18n: [
              'share',
              'quality',
              'quality_auto',
              'speed',
              'speed_normal',
              'subtitles'
            ].reduce(
              (res, key) => ({
                ...res,
                [key]: this.core.i18n.t(key)
              }),
              {} as Record<string, string>
            )
          })
        )
        this.close()
        this.mediaControl.$el.append(this.el)
        this.highlightCurrentLevel()
        this.highlightCurrentRate()
        this.highlightCurrentSubtitle()
      }
    }

    return this
  }
}
